import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A versatile loading indicator that can be used for authentication flows or data loading
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Whether the loading indicator should be visible
 * @param {string} props.message - Custom message to display
 * @param {boolean} props.fullScreen - Whether to show as a full-screen overlay
 * @param {boolean} props.transparent - Whether the background should be semi-transparent
 * @param {string} props.size - Size of the spinner ('sm', 'md', 'lg')
 */
const GlobalLoadingIndicator = ({ 
  isVisible = true, 
  message = 'Loading...', 
  fullScreen = true,
  transparent = true,
  size = 'md'
}) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();
  
  if (!isVisible) return null;
  
  // Size classes mapping
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16'
  };
  
  // Container classes based on fullScreen prop
  const containerClasses = fullScreen 
    ? 'fixed inset-0 z-50 flex items-center justify-center'
    : 'relative flex items-center justify-center min-h-[200px] p-4';
    
  // Background classes based on transparent prop
  const bgClasses = transparent
    ? `${semantic.background} bg-opacity-75`
    : semantic.background;
  
  return (
    <div className={`${containerClasses} ${fullScreen ? bgClasses : ''}`} data-testid="loading-indicator">
      <div className={`${semantic.contentCardBg} p-6 rounded-lg shadow-lg text-center`}>
        <div className={`animate-spin rounded-full border-b-2 ${semantic.primaryBorder} mx-auto ${sizeClasses[size]}`}></div>
        {message && <p className={`mt-4 ${semantic.contentCardText}`}>{replace(message)}</p>}
      </div>
    </div>
  );
};

GlobalLoadingIndicator.propTypes = {
  isVisible: PropTypes.bool,
  message: PropTypes.string,
  fullScreen: PropTypes.bool,
  transparent: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg'])
};

export default GlobalLoadingIndicator;

